import React, { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import styled from 'styled-components';
import { srConfig, email } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledContactSection = styled(motion.section)`
  max-width: 800px;
  margin: 0 auto var(--space-5xl);
  padding: 0 var(--container-padding);
  text-align: center;
  position: relative;

  /* Background text element */
  &::before {
    content: 'CONTACT';
    position: absolute;
    top: -10%;
    left: 50%;
    transform: translateX(-50%);
    font-size: clamp(120px, 25vw, 300px);
    font-weight: 700;
    color: rgba(0, 0, 0, 0.015);
    letter-spacing: 0.05em;
    z-index: 0;
    pointer-events: none;
    font-family: var(--font-display);
    -webkit-text-stroke: 1px rgba(0, 0, 0, 0.02);
    -webkit-text-fill-color: transparent;
    text-transform: uppercase;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    margin: 0 auto var(--space-4xl);
  }

  .overline {
    display: block;
    margin-bottom: var(--space-xl);
    color: var(--text-tertiary);
    font-family: var(--font-mono);
    font-size: var(--fz-md);
    font-weight: 400;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    opacity: 0.7;
  }

  .title {
    font-size: clamp(56px, 8vw, 96px);
    font-weight: 700;
    line-height: 0.9;
    letter-spacing: -0.04em;
    margin-bottom: var(--space-2xl);
    text-transform: uppercase;
    font-family: var(--font-display);
  }

  p {
    font-size: clamp(18px, 2vw, 22px);
    line-height: 1.9;
    color: var(--text-tertiary);
    opacity: 0.85;
    max-width: 600px;
    margin: 0 auto var(--space-3xl);
    font-weight: 300;
  }

  .email-link {
    ${({ theme }) => theme.mixins.bigButton};
    margin-top: var(--space-xl);
    width: 200px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.6, -0.05, 0.01, 0.99],
    },
  },
};

const Contact = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const isInView = useInView(revealContainer, { once: true, margin: '-100px' });

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  return (
    <StyledContactSection
      id="contact"
      ref={revealContainer}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={containerVariants}>
      <motion.h2 className="numbered-heading overline" variants={itemVariants}>
        What's Next?
      </motion.h2>

      <motion.h2 className="title" variants={itemVariants}>
        Get In Touch
      </motion.h2>

      <motion.p variants={itemVariants}>
        I'm always open to discussing new opportunities, collaborations, or just connecting! Whether you have a project idea, research opportunity, or just want to chat about AI/ML and full-stack development, feel free to reach out—I'll do my best to get back to you!
      </motion.p>

      <motion.a
        className="email-link"
        href={`mailto:${email}`}
        variants={itemVariants}
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}>
        Say Hello
      </motion.a>
    </StyledContactSection>
  );
};

export default Contact;
